import React from 'react';
import { X } from 'lucide-react';
import { Text } from '@cotera/client/app/components/ui';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { cn } from '@cotera/client/v0/lib/utils';
import { NODE_ACCENTS, type NodeAccent } from './node-accent';

/**
 * Config-panel chrome for a node/trigger: fills its parent with a header →
 * scrollable body → sticky footer. The footer stays pinned (body scrolls, footer
 * stays) so Save/Cancel never scroll away on long forms. Renders nothing when
 * `open` is false.
 *
 * Pass `onClose` only when the host provides no dismiss chrome of its own — the
 * floating canvas inspector does, the artifact panel does not (it has its own
 * close button, and a second one in the header would just be a duplicate).
 */
export const WorkflowEditPanel: React.FC<{
  open: boolean;
  icon?: React.ReactNode;
  accent?: NodeAccent;
  badge?: string;
  title: string;
  subtitle?: string;
  footer?: React.ReactNode;
  onClose?: () => void;
  children: React.ReactNode;
}> = ({
  open,
  icon,
  accent = 'neutral',
  badge,
  title,
  subtitle,
  footer,
  onClose,
  children,
}) => {
  const accentStyle = NODE_ACCENTS[accent];

  if (!open) {
    return null;
  }

  return (
    <div
      data-slot="workflow-detail-panel"
      // Fill the layer via absolute insets rather than `h-full` — a percentage
      // height collapses to content height inside an `overflow-auto` ancestor,
      // which floats short forms up under the header and leaves a gap below.
      className="absolute inset-0 flex flex-col overflow-hidden bg-card"
    >
      <div className="flex items-start gap-3 border-b px-5 py-3.5">
        {icon !== undefined && icon !== null ? (
          <span
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-md [&_svg]:h-4 [&_svg]:w-4',
              accentStyle.chip
            )}
          >
            {icon}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <Text.P className="truncate font-medium leading-tight">
            {title}
          </Text.P>
          {subtitle !== undefined && subtitle !== '' ? (
            <Text.Caption className="text-muted-foreground mt-0.5 block truncate">
              {subtitle}
            </Text.Caption>
          ) : null}
        </div>
        {badge !== undefined && badge !== '' ? (
          <Badge
            variant={accentStyle.badge}
            size="sm"
            className="mt-0.5 shrink-0"
          >
            {badge}
          </Badge>
        ) : null}
        {onClose !== undefined ? (
          <button
            type="button"
            title="Close"
            aria-label="Close"
            onClick={onClose}
            className="text-muted-foreground hover:bg-muted hover:text-foreground -mr-1.5 flex size-7 shrink-0 items-center justify-center rounded-md transition-colors"
          >
            <X className="size-4" />
          </button>
        ) : null}
      </div>

      {/* `overflow-x-hidden` is load-bearing, not cosmetic: with only
          `overflow-y-auto` the CSS spec forces the default `overflow-x: visible`
          to compute as `auto`, so the vertical scrollbar's own reserved width —
          or any child's focus ring — pops a spurious horizontal scrollbar. This
          body only ever scrolls vertically; its children wrap. */}
      <div className="mx-auto min-h-0 w-full max-w-2xl flex-1 overflow-x-hidden overflow-y-auto px-5 py-4">
        {children}
      </div>

      {footer !== undefined && footer !== null ? (
        <div className="flex flex-row justify-end gap-2 border-t bg-card px-5 py-3">
          <div className="mx-auto flex w-full max-w-2xl flex-row justify-end gap-2">
            {footer}
          </div>
        </div>
      ) : null}
    </div>
  );
};
